import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, AfterViewInit } from '@angular/core';

import { overlayConfigFactory } from 'ngx-modialog-11';
import { Modal, BSModalContext } from 'ngx-modialog-11/plugins/bootstrap';

import { MessageBoxComponent } from '../../dialogs/message-box/message-box.component';
import { PdfCreateService } from '../../services/pdf/pdf-create.service';

import { ImageUploadService } from './image-upload.service';
import { _tr } from '@app/extensions';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.scss'],
  providers: [ImageUploadService],
})
export class ImageUploadComponent implements OnInit, AfterViewInit {
  @Input() model;

  @Input() description;
  @Input() maxFileSize: number;

  @Input()
  buttonCaption = 'Upload';

  @Input()
  fileTooLargeMessage: string;

  @Input('extensions')
  supportedExtensions: string[];

  @Output()
  onImageUploadFinish = new EventEmitter<any>();

  @Output()
  onRemove = new EventEmitter<any>();

  @ViewChild('input')
  private inputElementRef: ElementRef;

  constructor(
    private modal: Modal,
    private imageUploadService: ImageUploadService,
    private pdfCreateService: PdfCreateService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.supportedExtensions = this.supportedExtensions ? this.supportedExtensions.map((ext) => `image/${ext}`) : ['image/*'];
  }

  ngAfterViewInit() {}

  fileChange(files: FileList) {
    if (!files) {
      return;
    }

    const file = files[0];

    if (!file) {
      return;
    }

    this.uploadFile(file);
  }

  clear(): void {
    this.onRemove.emit({
      data: this.model.data,
    });

    this.model.data = null;
    this.inputElementRef.nativeElement.value = '';
  }

  private uploadFile(file: File) {
    if (this.maxFileSize && file.size > this.maxFileSize) {
      this.modal.open(
        MessageBoxComponent,
        overlayConfigFactory(
          {
            description: this.translate.instant(_tr('An image was too large and was not uploaded.  The maximum file size is {{param1}}Mb.'), {
              param1: this.maxFileSize ? this.maxFileSize / 1024 / 1024 : '',
            }),
            buttons: [
              {
                text: 'Ok',
              },
            ],
            onClose: () => {
              this.inputElementRef.nativeElement.value = '';
            },
          },
          BSModalContext,
        ),
      );

      return;
    }

    if (this.supportedExtensions.indexOf(file.type) == -1) {
      this.modal.open(
        MessageBoxComponent,
        overlayConfigFactory(
          {
            description: 'Unsupported file type.',
            buttons: [
              {
                text: 'Ok',
              },
            ],
            onClose: () => {
              this.inputElementRef.nativeElement.value = '';
            },
          },
          BSModalContext,
        ),
      );

      return;
    }

    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onloadend = () => {
      this.model.pending = true;
      this.pdfCreateService
        .createPdf(file)
        .pipe(
          finalize(() => {
            this.model.pending = false;
          }),
        )
        .subscribe(
          (resonse) => {
            this.model.data = reader.result;
            this.onResponse(resonse);
          },
          (error) => {
            this.onError(error);
          },
        );
    };
  }

  private onError(response?) {
    this.clear();
    this.modal.open(
      MessageBoxComponent,
      overlayConfigFactory(
        {
          description: 'Could not upload image to server',
          classes: 'notitification-popup error-popup',
          buttons: [
            {
              text: 'Ok',
            },
          ],
          onClose: () => {},
        },
        BSModalContext,
      ),
    );
  }

  private onResponse(fileId) {
    if (!fileId) {
      this.onError();
    }

    const image = new Image();

    image.onload = () => {
      this.model.image = image;

      this.onImageUploadFinish.emit({
        data: this.model.data,
        fileId: fileId,
      });
    };

    image.src = this.model.data;
  }
}
